import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import HeaderNav from "@/components/HeaderNav";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, DollarSign, Bookmark, Trophy, Copy, Twitter, Share2, Calendar } from "lucide-react";

function num(n) {
  if (n === null || n === undefined) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function PublicProfile() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/u/${handle}`).then(r => setData(r.data)).catch(() => setErr(true));
  }, [handle]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const shareTweet = () => {
    if (!data) return;
    const text = `Building real side hustles on @ajobforsure 💸

$${num(data.stats.total_earned)} earned so far
${data.stats.saved_count} hustles on my shortlist
${data.stats.wins_count} wins logged

My proof: ${shareUrl}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (err) return (
    <>
      <HeaderNav/>
      <div className="max-w-2xl mx-auto py-24 text-center px-4">
        <h1 className="font-display text-3xl font-black">Profile not found</h1>
        <p className="text-[var(--stone)] mt-2">This handle doesn't exist or the profile is private.</p>
        <Link to="/" className="btn-primary mt-6 inline-flex">Home</Link>
      </div>
    </>
  );
  if (!data) return <><HeaderNav/><div className="max-w-4xl mx-auto p-12 text-center text-[var(--stone)]">Loading…</div></>;

  return (
    <div className="min-h-screen">
      <HeaderNav/>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 lg:py-16">
        {/* HERO */}
        <div className="card-warm p-8 sm:p-12 relative overflow-hidden" style={{background:"var(--forest)", color:"var(--alabaster)"}}>
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-12 h-12 rounded-full bg-[var(--copper)] flex items-center justify-center font-display font-black text-xl">
                  {data.name[0]?.toUpperCase()}
                </span>
                {data.pro && <span className="tag-pill" style={{background:"var(--copper)", color:"#fff", border:"none"}}>
                  <Sparkles size={12}/> Pro
                </span>}
              </div>
              <h1 className="font-display text-4xl sm:text-6xl font-black leading-tight" data-testid="profile-name">{data.name}</h1>
              <div className="font-mono text-sm opacity-80 mt-1">@{data.handle}</div>
              {data.bio && <p className="mt-4 max-w-lg opacity-90">{data.bio}</p>}
              {data.twitter && (
                <a href={`https://twitter.com/${data.twitter}`} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 mt-4 text-sm underline hover:text-[var(--copper)]">
                  <Twitter size={14}/> @{data.twitter}
                </a>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={shareTweet} className="btn-copper" data-testid="tweet-profile-btn">
                <Twitter size={16}/> Share on Twitter
              </button>
              <button onClick={copyLink} className="btn-outline !border-white/40 !text-white/90 hover:!bg-white/10 hover:!text-white" data-testid="copy-profile-btn">
                <Copy size={14}/> Copy link
              </button>
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <div className="card-warm p-5">
            <DollarSign size={20} className="text-[var(--copper)]"/>
            <div className="caption mt-2">Total earned</div>
            <div className="font-display text-3xl font-black mt-1 font-mono" data-testid="stat-earned">${num(data.stats.total_earned)}</div>
          </div>
          <div className="card-warm p-5">
            <Trophy size={20} className="text-[var(--copper)]"/>
            <div className="caption mt-2">Wins logged</div>
            <div className="font-display text-3xl font-black mt-1 font-mono">{data.stats.wins_count}</div>
          </div>
          <div className="card-warm p-5">
            <Bookmark size={20} className="text-[var(--copper)]"/>
            <div className="caption mt-2">Shortlist</div>
            <div className="font-display text-3xl font-black mt-1 font-mono">{data.stats.saved_count}</div>
          </div>
          <div className="card-warm p-5">
            <Calendar size={20} className="text-[var(--copper)]"/>
            <div className="caption mt-2">Entries logged</div>
            <div className="font-display text-3xl font-black mt-1 font-mono">{data.stats.entries}</div>
          </div>
        </div>

        {/* TOP STREAMS */}
        {data.top_streams.length > 0 && (
          <div className="card-warm p-6 sm:p-8 mt-6">
            <div className="caption">Top earning streams</div>
            <h2 className="font-display text-2xl font-bold mt-2 mb-4">Where the money's coming from</h2>
            <div className="space-y-2">
              {data.top_streams.map((s, i) => (
                <div key={s.name} className="flex items-center justify-between p-3 rounded-xl border border-[var(--border-warm)]">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl font-black text-[var(--copper)]">{i+1}</span>
                    <div className="font-semibold">{s.name}</div>
                  </div>
                  <div className="font-mono font-bold text-[var(--forest)]">${num(s.earned)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SAVED HUSTLES */}
        {data.saved_hustles.length > 0 && (
          <div className="mt-6">
            <div className="caption">Current shortlist</div>
            <h2 className="font-display text-2xl font-bold mt-2 mb-4">Hustles I'm exploring</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.saved_hustles.slice(0, 6).map(h => (
                <Link key={h.id} to={`/hustle/${h.id}`} className="card-warm p-4">
                  <span className="tag-pill">{h.category}</span>
                  <div className="font-display text-lg font-bold mt-2">{h.name}</div>
                  <div className="text-xs text-[var(--stone)] mt-1 font-mono">${h.monthly_low}–${h.monthly_high}/mo</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* RECENT WINS */}
        {data.recent_wins.length > 0 && (
          <div className="mt-6">
            <div className="caption">Recent wins</div>
            <h2 className="font-display text-2xl font-bold mt-2 mb-4">Real progress</h2>
            <div className="space-y-3">
              {data.recent_wins.map(w => (
                <div key={w.id} className="card-warm p-4">
                  <div className="flex justify-between flex-wrap gap-2">
                    <div className="font-display text-lg font-bold">{w.title}</div>
                    {w.earnings && <span className="font-mono text-[var(--copper)] font-bold">+${num(w.earnings)}</span>}
                  </div>
                  <p className="text-sm text-[var(--stone)] mt-1">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="card-warm p-8 mt-8 text-center" style={{background:"var(--alabaster-2)"}}>
          <h2 className="font-display text-2xl font-black">Want your own hustle proof page?</h2>
          <p className="text-[var(--stone)] mt-2">Take the 60-sec quiz, get matched, log your earnings, share your growth.</p>
          <Link to="/" className="btn-primary mt-5 inline-flex" data-testid="profile-signup-cta">
            Start free on ajobforsure <Share2 size={16}/>
          </Link>
        </div>
      </div>
    </div>
  );
}
